import * as React from 'react';
import { useContext, useEffect, useReducer, useState } from 'react';
import {
  Button, Checkbox, Dialog, DialogContent, DialogTitle, FormControl,
  FormControlLabel, FormLabel, Grid, IconButton, InputLabel,
  MenuItem, Select, TextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { getApiBaseUrl, getAssetsBaseUrl } from '../../utils/env';
import { apiGetPlayer } from '../../store/playlistSlice';
import { OrganisationContext } from '../../App';
import { initialPlayerState } from '../playlist-detail/PlaylistCreative';
import { useDispatch } from 'react-redux';
import { CreativeTagState, PlayerState } from '../types/playlist';
import { useParams } from 'react-router-dom';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { DroppableFile } from '../common/DropableFile';
import { creativeUrl, dspTag } from '../utils/dspTag';

type Action = {
  type: 'UPDATE';
  payload: Partial<PlayerState>;
} | {
  type: 'RESET';
}

const reducer = (state: PlayerState, action: Action): PlayerState => {
  switch (action.type) {
    case 'UPDATE':
      return { ...state, ...action.payload };
    case 'RESET':
      return initialPlayerState;
    default:
      return state;
  }
};

const upload = async (prefix: string, file: File) => {
  const formData = new FormData();
  formData.append('file', file as Blob);
  const cacheControl = 'max-age=31536000, public';

  const {
    data: {
      putObjectSignedUrl, filename
    }
  } = await axios.post(`${getApiBaseUrl()}/playlist/generate-upload-url`, {
    filename: file?.name,
    contentType: file.type,
    pathPrefix: prefix,
    cacheControl
  });

  await axios({
    method: 'put',
    url: putObjectSignedUrl,
    data: file,
    headers: {
      'Content-Type': file.type,
      'Cache-Control': cacheControl,
    },
    onUploadProgress: (progressEvent) => {
      const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      console.log(`Upload Progress: ${progress}%`);
    },
  }).catch((message) => {
    // todo
  });
  return filename;
};

const CloseBtnRoot = styled('div')`
    position: absolute;
    right: 10px;
    top: 10px;
`;

const Root = styled('div')`
    padding: 20px 40px 0;
    position: relative;
`;

const TitleDialog = styled(DialogTitle)`
    position: relative;
    padding-top: 0;

    &:after {
        content: ' ';
        background-color: #ccc;
        width: calc(100% + 80px);
        height: 1px;
        position: absolute;
        left: -40px;
        bottom: 10px;
    }
`;

const Title = styled('div')`
    font-weight: bold;
    margin: 24px 0 0 0;
    color: black;
`;

type PlaylistCreativeDialogProps = {
  open: boolean;
  handleClose: () => void;
  creativeState: PlayerState;
}

export const PlaylistCreativeDialog = ({
  open,
  handleClose,
  creativeState
}: PlaylistCreativeDialogProps) => {
  const { playlist } = useParams();
  const { selectedOrganisation } = useContext(OrganisationContext);
  const [state, dispatch] = useReducer(reducer, creativeState);
  const dispatchRedux = useDispatch();
  const [fileImageTray, setCurrentFileImageTray] = useState<File | string>('');
  const [fileEndSlate, setFileEndSlate] = useState<string | File>('');

  const reset = () => {
    dispatch({ type: 'RESET' });
    setFileEndSlate('');
    setCurrentFileImageTray('');
  };

  const update = (payload: Partial<PlayerState>) => dispatch({
    type: 'UPDATE',
    payload
  });

  useEffect(() => {
    update({
      playlist_uuid: playlist,
    });
  }, [playlist]);

  useEffect(() => {
    setFileEndSlate(creativeState.end_slate_image_filename ? `${getAssetsBaseUrl()}/end-slates/${creativeState.end_slate_image_filename}` : '');
    setCurrentFileImageTray(creativeState.tray_image_filename ? `${getAssetsBaseUrl()}/short-tray/${creativeState.tray_image_filename}` : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      ...state,
    };

    if (fileImageTray instanceof File) {
      const imageTrayFilename = await upload('short-tray', fileImageTray);
      update({
        tray_image_filename: imageTrayFilename
      });
      payload['tray_image_filename'] = imageTrayFilename;
      // to check if no change, we preferred to not send the field.
    } else if (fileImageTray.includes(state.tray_image_filename as string)) {
      delete payload['tray_image_filename'];
    } else {
      payload['tray_image_filename'] = fileImageTray;
    }

    if (fileEndSlate instanceof File) {
      const endSlateFilename = await upload('end-slates', fileEndSlate);
      update({
        end_slate_image_filename: endSlateFilename
      });
      // update state is async, need this workaround to update the payload immediately
      payload['end_slate_image_filename'] = endSlateFilename;
    } else if (fileEndSlate.includes(state.end_slate_image_filename as string || '')) {
      // do nothing for this field
      delete payload['end_slate_image_filename'];
    } else {
      payload['end_slate_image_filename'] = fileEndSlate;
    }

    payload['impression_trackers'] = payload['impression_trackers'].filter((x) => x.url);
    payload['lumen_trackers'] = payload['lumen_trackers'].filter((x) => x.mid);

    const errorHandler = (ex) => {
      console.log('>>> ', ex);
      if (Array.isArray(ex)) {
        alert(ex.join('\n'));
      } else {
        alert('Sorry, unable to complete the previous request.');
      }
    };

    if (state.id) {
      await axios
        .put(`${getApiBaseUrl()}/playlist/${selectedOrganisation}/${playlist}/player/${state.id}`, payload).catch(errorHandler);
    } else {
      await axios
        .post(`${getApiBaseUrl()}/playlist/${selectedOrganisation}/${playlist}/player`, payload).catch(errorHandler);
    }

    // update tag creative if the creative contain tags
    if (state.id) {
      const { data: tags } = await axios.get(`${getApiBaseUrl()}/playlist-tag/${selectedOrganisation}/${state.id}`);
      for (const tag of tags as CreativeTagState[]) {
        const url = creativeUrl(state, selectedOrganisation as string);
        const creativeTag = dspTag(tag.dsp_name, url, state.click_thru_url);
        await axios.patch(`${getApiBaseUrl()}/playlist-tag/tag/${selectedOrganisation}/${playlist}/${tag.id}`, {
          tag: creativeTag,
          id: tag.id,
        });
      }
    }

    reset();

    update({
      playlist_uuid: playlist,
    });

    dispatchRedux(apiGetPlayer({
      publisher: selectedOrganisation,
      playlistId: playlist
    }));

    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth={true} maxWidth={'lg'}>
      <Root>
        <CloseBtnRoot>
          <IconButton onClick={handleClose}>
            <CloseIcon/>
          </IconButton>
        </CloseBtnRoot>
        <TitleDialog>
          <Title>{state.id ? 'Edit creative' : 'Add creative'}</Title>
        </TitleDialog>
        <DialogContent>
          <form onSubmit={onSubmit}>
            <Grid container spacing={2} style={{ marginTop: 0 }}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <TextField
                    label="Name"
                    variant="outlined"
                    type={'text'}
                    required
                    id="playerName"
                    value={state.name}
                    onChange={({ target: { value } }) => update({
                      name: value
                    })}/>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel required id="videoSize">Number of videos to play</InputLabel>
                  <Select required value={state.video_size}
                          label={'Number of videos to play'}
                          onChange={({ target: { value } }) => {
                            update({
                              video_size: value as number
                            });
                          }}>
                    {[1, 2].map((x) =>
                      <MenuItem
                        key={x}
                        value={x}
                      >
                        {x}
                      </MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <TextField
                    label="Click thru URL"
                    variant="outlined"
                    type={'url'}
                    id="clickThruUrl"
                    inputProps={{
                      pattern: 'https?:\\/\\/(www\\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\\.[a-zA-Z0-9()]{1,6}\\b([-a-zA-Z0-9()@:%_\\+.~#?&//=]*)'
                    }}
                    value={state.click_thru_url}
                    onChange={({ target: { value } }) => update({ click_thru_url: value })}/>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  {state.impression_trackers.map(({ tag, event, url }, index) => (<>
                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <TextField
                          label={`Impression URL (${index + 1})`}
                          variant="outlined"
                          type={'url'}
                          inputProps={{
                            pattern: 'https?:\\/\\/(www\\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\\.[a-zA-Z0-9()]{1,6}\\b([-a-zA-Z0-9()@:%_\\+.~#?&//=]*)'
                          }}
                          id="impressionUrl"
                          value={url}
                          onChange={({ target: { value } }) => {
                            update({
                              impression_trackers: state.impression_trackers.map((item, i) => {
                                if (i !== index) return item;
                                return {
                                  ...item,
                                  url: value
                                };
                              })
                            });
                          }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={2}>
                      <FormControl fullWidth>
                        <InputLabel id="impressionEventTypeLabel">Event trigger</InputLabel>
                        <Select
                          id="impressionEventType"
                          labelId="impressionEventTypeLabel"
                          label={'Event trigger'}
                          value={event || 'loadstart'}
                          onChange={({ target: { value } }) => {
                            update({
                              impression_trackers: state.impression_trackers.map((item, i) => {
                                if (i !== index) return item;
                                return {
                                  ...item,
                                  event: value
                                };
                              })
                            });

                          }}
                        >
                          {['loadstart', 'videostart', 'componentrender', 'inview'].map((e) =>
                            <MenuItem
                              key={e} value={e}>{e}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={2}>
                      <FormControl fullWidth>
                        <InputLabel id="impressionHTMLTagLabel">HTML tag</InputLabel>
                        <Select
                          id="impressionHTMLTag"
                          labelId="impressionHTMLTagLabel"
                          label={'Event trigger'}
                          value={tag || 'img'}
                          onChange={({ target: { value } }) => {
                            update({
                              impression_trackers: state.impression_trackers.map((item, i) => {
                                if (i !== index) return item;
                                return {
                                  ...item,
                                  tag: value
                                };
                              })
                            });

                          }}
                        >
                          {['iframe', 'img'].map((e) => <MenuItem
                            key={e} value={e}>{e}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={2}>
                      <Grid container justifyContent={'center'} alignItems={'center'}>
                        {state.impression_trackers.length > 1 && <Grid item>
                          <IconButton
                            color="error"
                            onClick={() => {
                              update({
                                impression_trackers: state.impression_trackers.filter((_, i) => i !== index)
                              });
                            }}
                          >
                            <RemoveCircleIcon/>
                          </IconButton>
                        </Grid>}
                        <Grid item>
                          <IconButton
                            color="primary"
                            onClick={() => {
                              const head = state.impression_trackers.slice(0, index + 1);
                              const tail = state.impression_trackers.slice(index + 1);
                              update({
                                impression_trackers: [...head, initialPlayerState.impression_trackers[0], ...tail]
                              });
                            }}
                          >
                            <AddCircleIcon/>
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Grid>
                  </>))}
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={2} justifyContent={'flex-start'}>
                  {state.lumen_trackers.map(({ type, mid }, index) => (<>
                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <TextField
                          label={`Lumen Measurement ID`}
                          variant="outlined"
                          type={'text'}
                          id={`mid${index}`}
                          value={mid}
                          onChange={({ target: { value } }) => {
                            update({
                              lumen_trackers: state.lumen_trackers.map((item, i) => {
                                if (i !== index) return item;
                                return {
                                  ...item,
                                  mid: value
                                };
                              })
                            });
                          }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={2}>
                      <FormControl fullWidth>
                        <InputLabel id="impressionEventTypeLabel">Type</InputLabel>
                        <Select
                          id={`lumenType${index}`}
                          labelId={`lumenTypeLabel${index}`}
                          label={'Tracking type'}
                          value={type || 'video'}
                          onChange={({ target: { value } }) => {
                            update({
                              lumen_trackers: state.lumen_trackers.map((item, i) => {
                                if (i !== index) return item;
                                return {
                                  ...item,
                                  type: value
                                };
                              })
                            });

                          }}
                        >
                          {['video', 'preroll', 'component'].map((e) => <MenuItem
                            key={e} value={e}>{e}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={2}>
                      <Grid container justifyContent={'center'} alignItems={'center'}>
                        {state.lumen_trackers.length > 1 && <Grid item>
                          <IconButton
                            color="error"
                            onClick={() => {
                              update({
                                lumen_trackers: state.lumen_trackers.filter((_, i) => i !== index)
                              });
                            }}
                          >
                            <RemoveCircleIcon/>
                          </IconButton>
                        </Grid>}
                        <Grid item>
                          <IconButton
                            color="primary"
                            onClick={() => {
                              const head = state.lumen_trackers.slice(0, index + 1);
                              const tail = state.lumen_trackers.slice(index + 1);
                              update({
                                lumen_trackers: [...head, initialPlayerState.lumen_trackers[0], ...tail]
                              });
                            }}
                          >
                            <AddCircleIcon/>
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Grid>
                  </>))}
                </Grid>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <FormControlLabel control={<Checkbox
                    onChange={({ target: { checked } }) => {
                      update({ enabled_tray: checked });
                    }}
                    checked={state.enabled_tray}
                  />}
                                    label="Tray enabled"/>
                </FormControl>
              </Grid>
              {state.enabled_tray && <>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <FormControlLabel control={<Checkbox
                      onChange={({ target: { checked } }) => {
                        update({ click_thru_on_tray_only: checked });
                      }}
                      checked={state.click_thru_on_tray_only}
                    />}
                                      label="Click thru on tray only"/>
                  </FormControl>
                </Grid>
              </>}
              {state.enabled_tray && <><Grid item xs={6}>
                <FormControl fullWidth>
                  <TextField
                    label="Tray colour"
                    variant="outlined"
                    type={'text'}
                    id="trayColour"
                    value={state.tray_colour}
                    onChange={({ target: { value } }) => update({ tray_colour: value })}/>
                </FormControl>
              </Grid>
                <Grid item xs={6}></Grid>
              </>}
              {state.enabled_tray && <><Grid item xs={6}>
                <FormControl fullWidth>
                  <FormLabel id="logo-label">Image tray</FormLabel>
                  <DroppableFile binaryOrFileUrl={fileImageTray}
                                 setBinaryOrFileUrl={setCurrentFileImageTray}/>
                </FormControl>
              </Grid></>}
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <FormLabel id="logo-label">Image end slate</FormLabel>
                  <DroppableFile binaryOrFileUrl={fileEndSlate}
                                 setBinaryOrFileUrl={setFileEndSlate}/>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Grid container justifyContent={'space-between'}>
                  <Grid item>
                    <Button color="inherit" variant="contained" onClick={reset}
                            type={'button'}>Reset</Button>
                  </Grid>
                  <Grid item>
                    <Button color="primary" variant="contained" type={'submit'}>Save</Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
      </Root>
    </Dialog>
  );
};
